import { Link } from "react-router-dom";

import { Bullets, Callout, Contact, LegalHeader, Mail, P, Section } from "@/components/legal";
import { useSeoMeta } from "@/lib/useSeoMeta";

// Terms of Service for SkillPatch, operated by LatentForce Intelligence Private Limited.
// Adapted from latentforce.ai/legal/terms for SkillPatch (a registry for agentic skills):
// fees/subscriptions/trials, LatentGraph "Outputs", and Enterprise references have been removed,
// and cross-references use section names (the § numbers are not shown).

const EFFECTIVE = "01/07/2026";

export default function TermsPage() {
  useSeoMeta({ title: "Terms of Service — SkillPatch", path: "/legal/terms" });

  return (
    <>
      <div className="grid-backdrop" aria-hidden />
    <main className="max-w-6xl px-6 py-12 sm:pl-16 sm:pr-10 lg:pl-28">
      <LegalHeader title="Terms of" accent="Service" effective={EFFECTIVE}>
        A binding agreement between you (“Customer”, “you”) and{" "}
        <strong className="text-text/80">LatentForce Intelligence Private Limited</strong> (“LatentForce”, “we”, “us”)
        that governs your use of SkillPatch and any associated services, APIs, CLI tools, and websites (collectively, the
        “Service”). By creating an account, accessing, or using the Service, you agree to these Terms.
      </LegalHeader>

      <Section title="Eligibility">
        <P>
          You must be at least 18 years old and able to form a binding contract. If you use the Service on behalf of an
          organisation, you represent that you have authority to bind that organisation, and "you" refers to that
          organisation.
        </P>
      </Section>

      <Section title="License Grant">
        <P>
          Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable
          license to access and use the Service.
        </P>
      </Section>

      <Section title="Your Content — and Our Training Posture">
        <P>
          <strong>"Customer Content"</strong> means any skills (SKILL.md and supporting files), suggested changes,
          archives, descriptions, or other materials you submit to or generate through the Service.
        </P>
        <P>
          You retain all right, title, and interest in and to Customer Content. You grant us a limited license to host,
          copy, process, transmit, and display Customer Content solely to provide the Service to you — including, for
          skills you choose to publish, making them publicly available for others to view and install.
        </P>
        <Callout>
          WE WILL NOT USE CUSTOMER CONTENT TO TRAIN, FINE-TUNE, OR EVALUATE ANY AI MODELS, AND WE WILL NOT PERMIT ANY
          THIRD PARTY (INCLUDING OUR LLM PROVIDER) TO DO SO, UNLESS YOU EXPLICITLY OPT IN IN WRITING.
        </Callout>
        <P>
          We use a third-party LLM provider (AWS Bedrock) to review skills and suggested changes for structure and
          security. Customer Content is processed under zero-retention configurations where available. Details are in our{" "}
          <Link to="/legal/privacy" className="text-accent underline-offset-2 transition hover:underline">
            Privacy Policy
          </Link>
          .
        </P>
      </Section>

      <Section title="Acceptable Use">
        <P>You will not:</P>
        <Bullets
          items={[
            "Reverse engineer, decompile, or attempt to extract the source code or model weights of the Service.",
            "Use the Service to build a competing product.",
            "Publish or submit skills or other Customer Content you do not have the rights to submit.",
            "Publish skills that are malicious, deceptive, or designed to harm, mislead, or exfiltrate data from users or their agents.",
            "Use the Service to violate any law, infringe any intellectual property right, or distribute malware.",
            "Bypass any usage limit, rate limit, security review, or other security mechanism.",
            "Resell or sublicense the Service without our written permission.",
          ]}
        />
      </Section>

      <Section title="Intellectual Property">
        <P>
          <strong>Our IP.</strong> We retain all right, title, and interest in and to the Service, including all
          underlying software, review agents, UI, and documentation. No rights are granted to you except as expressly set
          out in these Terms.
        </P>
        <P>
          <strong>Your skills.</strong> You keep ownership of the skills and other Customer Content you publish. You are
          responsible for licensing your published skills appropriately for others to install and use.
        </P>
        <P>
          <strong>Third-party skills.</strong> Some skills listed on the registry are third-party, openly-licensed
          content that we do not own; they remain subject to their own licenses (see the Official Skills section).
        </P>
        <P>
          <strong>Feedback.</strong> If you give us suggestions or feedback about the Service, you grant us a perpetual,
          royalty-free license to use it without restriction.
        </P>
      </Section>

      <Section title="Official Skills">
        <P>
          Some skills in the registry are <strong>official skills</strong> that we import from publicly available GitHub
          repositories published under a permissive open-source license (such as MIT). For these:
        </P>
        <Bullets
          items={[
            "We preserve attribution to the original repository and author and link back to the source, as the applicable license requires.",
            "Your use of an imported skill is governed by that skill's upstream open-source license, not by these Terms, and we make no additional warranty over it.",
            "If you are the original author, you can verify via GitHub to consent to the listing, claim and maintain the skill (transferring it to your account), or request takedown. No account is required to consent or request takedown.",
            "We remove imported skills promptly on a valid takedown request or where the source license does not permit redistribution.",
          ]}
        />
      </Section>

      <Section title="Confidentiality">
        <P>
          Each party will protect the other's Confidential Information using the same care it uses for its own (and at
          least reasonable care), and will not disclose it except to personnel with a need to know who are bound by
          similar obligations.
        </P>
      </Section>

      <Section title="Data Protection">
        <P>
          Our processing of personal data is described in our{" "}
          <Link to="/legal/privacy" className="text-accent underline-offset-2 transition hover:underline">
            Privacy Policy
          </Link>
          . A Data Processing Addendum (DPA) is available on request at <Mail /> and incorporated into these Terms when
          executed.
        </P>
      </Section>

      <Section title="Security">
        <P>
          We maintain administrative, technical, and physical safeguards designed to protect Customer Content, including
          encryption in transit and at rest, access controls, and audit logging.
        </P>
      </Section>

      <Section title="Term and Termination">
        <P>These Terms apply for as long as you have an account. Either party may terminate:</P>
        <Bullets
          items={[
            <>
              <strong>You:</strong> at any time, by deleting your account or ceasing to use the Service.
            </>,
            <>
              <strong>Us, for cause:</strong> if you materially breach these Terms and do not cure within 14 days of
              notice (immediately for breaches of the Acceptable Use or Intellectual Property sections).
            </>,
            <>
              <strong>Us, for convenience:</strong> with 30 days' notice.
            </>,
          ]}
        />
        <P>
          On termination, your access ends, and we will delete Customer Content within 30 days unless retention is
          required by law.
        </P>
      </Section>

      <Section title="Warranties and Disclaimers">
        <P>
          EXCEPT AS EXPRESSLY STATED, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", AND WE DISCLAIM ALL WARRANTIES,
          EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. Skills
          are published by their authors; our automated review reduces but does not eliminate risk, and we do not warrant
          that any skill is safe, accurate, or fit for your purpose. You are responsible for reviewing skills before
          installing or relying on them.
        </P>
      </Section>

      <Section title="Limitation of Liability">
        <P>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY IS LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, OR DATA. EACH PARTY'S TOTAL LIABILITY UNDER
          THESE TERMS IS CAPPED AT THE AMOUNT YOU PAID US FOR THE SERVICE IN THE 12 MONTHS PRECEDING THE CLAIM, OR, IF YOU
          HAVE PAID NOTHING, ₹8,000 (APPROXIMATELY US$100). THESE LIMITS DO NOT APPLY TO (A) BREACHES OF THE ACCEPTABLE
          USE OR CONFIDENTIALITY SECTIONS, OR (B) INDEMNIFICATION OBLIGATIONS.
        </P>
      </Section>

      <Section title="Indemnification">
        <P>
          <strong>By us:</strong> We will defend you against third-party claims that the Service infringes a patent,
          copyright, trademark, or trade secret, and will pay damages finally awarded, provided you give us prompt notice
          and control of the defence.
        </P>
        <P>
          <strong>By you:</strong> You will defend us against third-party claims arising from (a) your Customer Content,
          (b) your breach of the Acceptable Use section, or (c) your violation of law.
        </P>
      </Section>

      <Section title="Governing Law and Disputes">
        <P>
          These Terms are governed by the <strong>laws of India</strong>, without regard to conflict-of-law rules. The
          courts at <strong>Bangalore, Karnataka</strong> have exclusive jurisdiction over any dispute that is not
          subject to arbitration.
        </P>
        <P>
          <strong>Arbitration.</strong> Any dispute arising out of or relating to these Terms that is not resolved through
          good-faith discussion within 30 days will be referred to arbitration under the{" "}
          <strong>Arbitration and Conciliation Act, 1996</strong>, before a sole arbitrator mutually appointed by the
          parties. The seat and venue of arbitration is <strong>Bangalore</strong>. The language of the arbitration is
          English. The arbitral award is final and binding.
        </P>
        <P>
          For Indian consumers, nothing in this Section limits your rights under the{" "}
          <strong>Consumer Protection Act, 2019</strong>.
        </P>
      </Section>

      <Section title="Changes">
        <P>
          We may update these Terms from time to time. Material changes will be notified to active users by email at least
          30 days before they take effect. Continued use of the Service after the effective date constitutes acceptance.
        </P>
      </Section>

      <Section title="Miscellaneous">
        <P>
          These Terms, together with the{" "}
          <Link to="/legal/privacy" className="text-accent underline-offset-2 transition hover:underline">
            Privacy Policy
          </Link>{" "}
          and any executed DPA, are the entire agreement between the parties. They supersede any prior agreement on the
          subject. If any provision is held unenforceable, the remainder remains in effect. Neither party may assign these
          Terms without the other's consent, except to an affiliate or in connection with a merger, acquisition, or sale
          of assets.
        </P>
      </Section>

      <Section title="Contact">
        <Contact
          lines={[
            <>
              <strong>LatentForce Intelligence Private Limited</strong> (CIN: U62011KA2025PTC205213)
            </>,
            "A company incorporated under the Companies Act, 2013.",
            "Registered office: 3rd Floor, Sree Gururaya Mansion, 8th Main, JP Nagar III Phase, Bangalore South, Bangalore - 560078, Karnataka",
            <>Legal: <Mail /></>,
            <>Support: <Mail /></>,
            <>Grievance Officer (DPDP Act 2023 / Consumer Protection Rules): <Mail /></>,
          ]}
        />
      </Section>
    </main>
    </>
  );
}
